import { SchemaService } from '@/pages/servicios/components/ServicioComponents';
import { ConversionBlock } from '@/pages/guias/components/GuiaComponents';
import { SchemaBreadcrumb, SchemaPriceOffer } from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link, useLocation } from 'react-router-dom';
import ReassuranceSection from '@/components/feature/ReassuranceSection';

export default function HosteleriaPage() {
  const location = useLocation();

  const breadcrumbItems = [
    { name: 'Inicio', url: 'https://toldos-malaga.es/' },
    { name: 'Toldos para hostelería', url: `https://toldos-malaga.es${location.pathname}` },
  ];

  return (
    <>
      <title>Toldos para Bares, Restaurantes y Terrazas en Málaga | ToldosMálaga</title>
      <meta name="description" content="Toldos a medida para bares, restaurantes y terrazas en Málaga. Instalación profesional, mantenimiento y respuesta rápida en temporada. Más mesas a la sombra, más clientes." />
      <meta name="keywords" content="toldos para bares Málaga, toldos terraza bar, toldos para restaurantes, toldos comerciales, toldos hostelería Málaga" />
      <link rel="canonical" href="https://toldos-malaga.es/toldos-hosteleria" />

      <SchemaBreadcrumb items={breadcrumbItems} />
      <SchemaService
        name="Toldos para bares, restaurantes y terrazas en Málaga"
        description="Instalación y mantenimiento de toldos para hostelería en Málaga. Toldos a medida para terrazas de bares y restaurantes con respuesta rápida."
        priceRange="desde 350"
      />
      <SchemaPriceOffer price={650} name="Instalación de toldo articulado para hostelería en Málaga" description="Toldo articulado de gran superficie (6m) para terrazas de bares y restaurantes." />
      <SchemaPriceOffer price={180} name="Toldo vertical lateral para hostelería en Málaga" description="Toldo vertical lateral para cerrar laterales de terrazas y proteger del viento." />
      <SchemaPriceOffer price={220} name="Motorización con sensor de viento para hostelería en Málaga" description="Motorización con sensor de viento para toldos de hostelería, protección automática." />
      <SchemaPriceOffer price={120} name="Mantenimiento anual de toldos para hostelería en Málaga" description="Contrato de mantenimiento anual para toldos de bares y restaurantes." />
      <meta property="og:title" content="Toldos para Bares, Restaurantes y Terrazas en Málaga | Toldos Málaga" />
      <meta property="og:description" content="Toldos a medida para bares, restaurantes y terrazas en Málaga. Instalación profesional, mantenimiento y respuesta rápida en temporada. Más mesas a la sombra, más clientes." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://toldos-malaga.es/toldos-hosteleria" />
      <meta property="og:image" content="https://toldos-malaga.es/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Toldos para hostelería en Málaga | Toldos Málaga" />
      <meta name="twitter:description" content="Toldos a medida para bares y restaurantes en Málaga. Más mesas a la sombra, más clientes." />
      <meta name="twitter:image" content="https://toldos-malaga.es/og-image.jpg" />

      <Navbar />
      <main className="min-h-screen bg-background-50">
        <nav aria-label="Breadcrumb" className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-28 pb-2">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-500 transition-colors duration-200">Inicio</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-700 font-medium">Toldos para hostelería</li>
          </ol>
        </nav>

        <ReassuranceSection />

        <article className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <header className="mb-10">
            <span className="inline-flex px-3 py-1 rounded-full border border-primary-200/60 bg-primary-100/60 text-primary-700 text-xs font-semibold font-label tracking-wide uppercase whitespace-nowrap mb-4">
              Hostelería
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-5">
              Toldos para bares,<br />restaurantes y terrazas<br />en Málaga
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-4xl">
              En Málaga, una terraza bien sombreada puede marcar la diferencia entre un local vacío y uno lleno. Con más de 300 días de sol al año, la sombra no es un lujo: es una necesidad para tu negocio. Instalamos toldos a medida para hostelería con materiales de alta resistencia al sol intenso, al viento de levante y al salitre de la costa malagueña.
            </p>
          </header>

          <div className="w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-10">
            <img
              src="/images/020-hosteleria-hero-01.webp"
              alt="Terraza de restaurante con toldos en Málaga"
              title="Toldos para hostelería en Málaga - Terrazas sombreadas"
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-8">
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  Más mesas a la sombra, más clientes
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-3">
                  En hostelería, cada metro cuadrado de terraza cuenta. Una terraza sin toldo en pleno agosto en Málaga es una terraza vacía. Con un toldo bien instalado, ganas espacio útil durante todo el año, incluso en las horas centrales del día cuando el sol pega más fuerte. Tus clientes se quedan más tiempo, consumen más y, sobre todo, vuelven.
                </p>
                <p className="text-foreground-600 leading-relaxed mb-3">
                  No todos los toldos son iguales. Para un bar o restaurante en Málaga, el toldo tiene que aguantar el sol directo de julio (que decolora las lonas baratas en una temporada), las rachas de viento de levante que pueden arrancar una estructura mal fijada y la humedad salina de la costa que oxida mecanismos y brazos metálicos. Por eso trabajamos solo con materiales de grado profesional y estructuras reforzadas.
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  Instalamos toldos de brazo recto para fachadas, toldos de brazo articulado para cubrir grandes superficies, y toldos verticales para cerrar laterales y proteger del viento. Cada instalación se estudia según la orientación de tu terraza y las horas de sol que recibe, para que la sombra esté donde tiene que estar cuando más se necesita.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-5">
                  Lo que ofrecemos a tu negocio
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-ruler-line', title: 'Instalación a medida', desc: 'Toldos adaptados a las dimensiones exactas de tu terraza, con estudio de orientación solar.' },
                    { icon: 'ri-shield-flash-line', title: 'Materiales profesionales', desc: 'Lonas microperforadas y acrílicas de alta resistencia al sol, brazos reforzados anticorrosión.' },
                    { icon: 'ri-timer-flash-line', title: 'Respuesta rápida', desc: 'Sabemos que en hostelería cada día cerrado es dinero perdido. Intervenimos en 24/48h.' },
                    { icon: 'ri-calendar-check-line', title: 'Mantenimiento anual', desc: 'Revisión antes de temporada alta, impermeabilización de lona y ajuste de tensores.' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3 p-4 rounded-xl bg-background-100/70 border border-background-200/70">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-primary-600 text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-5">
                  Precios orientativos para hostelería
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 px-4 font-semibold text-foreground-900">Tipo de instalación</th>
                        <th className="text-right py-3 px-4 font-semibold text-foreground-900 whitespace-nowrap">Desde</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      <tr className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-4 text-foreground-700">Toldo brazo recto para fachada (4m)</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600 whitespace-nowrap">350 €</td>
                      </tr>
                      <tr className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-4 text-foreground-700">Toldo articulado gran superficie (6m)</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600 whitespace-nowrap">650 €</td>
                      </tr>
                      <tr className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-4 text-foreground-700">Toldo vertical lateral (por unidad)</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600 whitespace-nowrap">180 €</td>
                      </tr>
                      <tr className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-4 text-foreground-700">Motorización con sensor de viento</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600 whitespace-nowrap">220 €</td>
                      </tr>
                      <tr className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-4 text-foreground-700">Mantenimiento anual (contrato)</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600 whitespace-nowrap">120 €</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-foreground-400 mt-3">
                  * Precios orientativos. El presupuesto final depende de las dimensiones exactas, tipo de lona y accesibilidad de la instalación. Solicita tu presupuesto sin compromiso.
                </p>
              </section>
            </div>

            <aside className="space-y-6">
              <ConversionBlock
                title="¿Tienes un bar o restaurante?"
                subtitle="Solicita una visita sin compromiso. Evaluamos tu terraza y te damos presupuesto en 24 horas."
              />
              <div className="p-5 rounded-xl bg-primary-100/50 border border-primary-200/40">
                <h3 className="text-sm font-semibold text-primary-800 mb-3">¿Por qué elegirnos?</h3>
                <ul className="space-y-2.5">
                  {[
                    'Especialistas en hostelería desde 2009',
                    'Más de 200 terrazas instaladas en Málaga',
                    'Garantía de 3 años en instalaciones profesionales',
                    'Atendemos urgencias en temporada alta',
                    'Presupuesto detallado sin compromiso',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}